/* ============================================================
   v7Media Premium Motion Engine
   IntersectionObserver-Reveals, Stagger, Count-Up, Scroll-Progress
   Respektiert prefers-reduced-motion. Kein externes Framework.
   ============================================================ */
(() => {
  'use strict';

  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const easeOut = (t) => 1 - Math.pow(1 - t, 4);

  /* Stagger: Eltern-Element [v7-stagger="0.08"] verteilt Delays auf Kinder */
  function stagger(scope) {
    scope.querySelectorAll('[v7-stagger]').forEach((parent) => {
      const step = parseFloat(parent.getAttribute('v7-stagger')) || 0.08;
      let i = 0;
      Array.from(parent.children).forEach((child) => {
        if (child.hasAttribute('v7-reveal')) {
          child.style.setProperty('--v7-delay', (i * step).toFixed(2) + 's');
          i += 1;
        }
      });
    });
  }

  /* Reveals — threshold niedrig, damit auch Anker-Sprünge sofort auslösen */
  function reveal(scope) {
    const els = scope.querySelectorAll('[v7-reveal]:not(.v7-in)');
    if (!els.length) return;
    if (REDUCED || !('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('v7-in'));
      return;
    }
    const io = new IntersectionObserver((entries, obs) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('v7-in');
        obs.unobserve(entry.target);
      });
    }, { threshold: 0.01, rootMargin: '0px 0px -4% 0px' });
    els.forEach((el) => io.observe(el));
  }

  /* Count-Up: [v7-count="1.8"] zählt Zahlen im Textinhalt hoch (de-DE Formate) */
  function counts(scope) {
    const els = Array.from(scope.querySelectorAll('[v7-count]:not([data-v7-done])'));
    if (!els.length) return;
    els.forEach((el) => { el.dataset.v7Done = '1'; });
    if (REDUCED || !('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver((entries, obs) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        obs.unobserve(entry.target);
        const el = entry.target;
        const raw = el.textContent.trim();
        const m = raw.match(/[\d.,]+/);
        if (!m) return;
        const numStr = m[0];
        let target;
        let decimals = 0;
        let thousands = false;
        if (/^\d{1,3}(\.\d{3})+$/.test(numStr)) {
          target = parseFloat(numStr.replace(/\./g, ''));
          thousands = true;
        } else if (numStr.indexOf(',') !== -1) {
          target = parseFloat(numStr.replace(',', '.'));
          decimals = (numStr.split(',')[1] || '').length;
        } else {
          target = parseFloat(numStr);
        }
        if (!isFinite(target)) return;
        const prefix = raw.slice(0, m.index);
        const suffix = raw.slice(m.index + numStr.length);
        const dur = (parseFloat(el.getAttribute('v7-count')) || 1.8) * 1000;
        const fmt = (v) => thousands
          ? Math.round(v).toLocaleString('de-DE')
          : v.toFixed(decimals).replace('.', ',');
        const t0 = performance.now();
        const tick = (now) => {
          const p = Math.min((now - t0) / dur, 1);
          el.textContent = prefix + fmt(target * easeOut(p)) + suffix;
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      });
    }, { threshold: 0.4 });
    els.forEach((el) => io.observe(el));
  }

  /* Scroll-Progress: [v7-progress] bekommt --v7-progress 0..1 (Timeline-Linie) */
  function progress() {
    const els = document.querySelectorAll('[v7-progress]');
    if (!els.length) return;
    if (REDUCED) {
      els.forEach((el) => el.style.setProperty('--v7-progress', '1'));
      return;
    }
    const update = () => {
      const vh = window.innerHeight;
      els.forEach((el) => {
        const r = el.getBoundingClientRect();
        const total = r.height + vh * 0.2;
        const passed = vh * 0.82 - r.top;
        const p = Math.max(0, Math.min(1, passed / total));
        el.style.setProperty('--v7-progress', p.toFixed(4));
      });
    };
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => { update(); ticking = false; });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  }

  const init = (scope) => { stagger(scope); reveal(scope); counts(scope); };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => { init(document); progress(); });
  } else {
    init(document);
    progress();
  }

  /* Theme-Editor: Sektionen neu initialisieren */
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();